import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
    "cursor-pointer inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-button text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 disabled:cursor-not-allowed [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
    {
        variants: {
            variant: {
                // Primary CTA — the canonical dark action button. Paints with the
                // --primary-cta token (#1F1817) + its lifted hover so every solid
                // dark button matches. Use this for Save / Analyze / Send / etc.
                default: "bg-primary-cta text-primary-cta-foreground hover:bg-primary-cta-hover",
                destructive:
                    "bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
                outline:
                    "border border-secondary-cta-border bg-secondary-cta text-secondary-cta-foreground hover:bg-secondary-cta-hover dark:bg-input/30 dark:border-input dark:hover:bg-input/50",
                secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
                ghost: "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
                link: "text-primary underline-offset-4 hover:underline"
            },
            size: {
                default: "h-9 px-4 py-2 has-[>svg]:px-3",
                sm: "h-8 rounded-button gap-1.5 px-3 has-[>svg]:px-2.5",
                // Compact page-header CTA (New Campaign / Add Contacts). Smaller
                // than `sm`; semibold label; tighter icon-to-text ratio (14px
                // glyphs vs the default 16px) so the icon doesn't dwarf the 12px
                // label; icons nudged +0.5px so they sit grounded on the line.
                xs: "h-7 rounded-button gap-1.5 px-2.5 text-[12px] font-semibold has-[>svg]:px-2 [&_svg:not([class*='size-'])]:size-3.5 [&_svg]:translate-y-[0.5px]",
                lg: "h-10 rounded-button px-6 has-[>svg]:px-4",
                icon: "size-9",
                "icon-sm": "size-8",
                "icon-lg": "size-10"
            }
        },
        defaultVariants: {
            variant: "default",
            size: "default"
        }
    }
)

const Button = React.forwardRef<
    HTMLButtonElement,
    React.ComponentProps<"button"> &
        VariantProps<typeof buttonVariants> & {
            asChild?: boolean
        }
>(({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"

    return <Comp ref={ref} data-slot="button" className={cn(buttonVariants({ variant, size, className }))} {...props} />
})

Button.displayName = "Button"

export { Button, buttonVariants }
